import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';
import type { AuthConfigOut } from '../types';
import { tokenRequest } from './config';

/**
 * Silent-first token acquisition. Extracted from the provider so it can be tested
 * against a fake `IPublicClientApplication` — MSAL itself cannot run in jsdom.
 */

/** True for the errors that a fresh interactive sign-in is the correct answer to. */
export function needsInteraction(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  // Not an InteractionRequiredAuthError, but the same remedy: MSAL raises this when the
  // account it was asked about is not in the cache at all — typically a cache cleared in
  // another tab. Without this it surfaces as a generic failure and the user is stuck.
  return error instanceof BrowserAuthError && error.errorCode === 'no_account_error';
}

/**
 * Interactive sign-in uses **redirect, not popup**. Popup is friendlier but unreliable
 * here: browsers block popups not opened from a click, and our interactive path is
 * reached from a background token refresh rather than a user gesture, so a popup would
 * be blocked exactly when it is most needed. Redirect also survives third-party-cookie
 * blocking and embedded webviews. The cost is losing in-memory state across the
 * redirect, which this read-only view has none of.
 */
export async function acquireToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
  config: AuthConfigOut,
): Promise<string> {
  const request = { ...tokenRequest(config), account };
  try {
    const result = await instance.acquireTokenSilent(request);
    return result.accessToken;
  } catch (error) {
    if (!needsInteraction(error)) throw error;
    // Navigates away and never resolves. The rejection below is unreachable in a real
    // browser but keeps the signature honest and covers a blocked navigation.
    await redirectToSignIn(instance, request);
    throw new Error('Redirecting to sign in...');
  }
}

/**
 * Starts an interactive sign-in, tolerating one that is already under way.
 *
 * An expired session is discovered by every in-flight request at once — the page loads
 * several in parallel — and MSAL holds a single interaction lock: the first caller
 * navigates, every other one is rejected with `interaction_in_progress`. That rejection
 * means "the redirect you wanted is already happening", not a failure; surfacing it put
 * a raw MSAL error on screen for the moment before the page left. Any other error still
 * propagates.
 */
export async function redirectToSignIn(
  instance: IPublicClientApplication,
  request: RedirectRequest,
): Promise<void> {
  try {
    await instance.acquireTokenRedirect(request);
  } catch (error) {
    if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') return;
    throw error;
  }
}
